import { PaymentMethodsAccepted } from '@/models/configuration/ProviderConfiguration';

// POS tender fields live under this prefix.
export const TENDER_PREFIX = 'posConfiguration.tenderConfiguration.';
export const TENDER_TOGGLE = `${TENDER_PREFIX}useTouchTabTenderId`;
export const TOUCHTAB_TENDER_ID = `${TENDER_PREFIX}touchTabTenderId`;
export const GIFT_CARD_TENDER_ID = `${TENDER_PREFIX}giftCardTenderId`;
export const CASH_TENDER_ID = `${TENDER_PREFIX}cashTenderId`;

// The tenders that are not card brands, each with the payment method that calls for it. A tender id is only
// the POS-side mapping for a method, so there is nothing to fill in for a method the location does not take.
const NON_CARD_TENDERS = [
    { path: GIFT_CARD_TENDER_ID, method: PaymentMethodsAccepted.GiftCard },
    { path: CASH_TENDER_ID, method: PaymentMethodsAccepted.Cash },
];

/**
 * Which tender ids the onboarding form shows, for a given tender mode and set of accepted payment methods.
 *
 * Lives here, and is tested, because the two modes are asymmetric and that asymmetry is a trap. Per-brand
 * mode shows every tender path the schema carries; TouchTab mode shows a FIXED list. So a tender id that is
 * not a card brand - Gift Card, Cash - has to be named in the TouchTab list or it silently disappears the
 * moment the toggle goes on, while looking perfectly fine in the mode you happened to test.
 *
 * The tender-type toggle exists to send every CARD payment to the POS under one tender. Gift card and cash
 * are their own tenders in the POS either way, so it is not what governs them: they follow their ACCEPTED
 * PAYMENT METHOD instead, and appear in both modes.
 */
export function visibleTenderIds(
    tenderPaths: readonly string[],
    useTouchTab: boolean,
    acceptedMethods: number,
): string[] {
    const ids = useTouchTab
        ? [TOUCHTAB_TENDER_ID, GIFT_CARD_TENDER_ID, CASH_TENDER_ID].filter((p) => tenderPaths.includes(p))
        // The toggle is a control, not a value to fill in. If it leaked into this list it would be counted
        // as a field the client still has to complete, and onboarding could never reach 100%.
        : tenderPaths.filter((p) => p !== TENDER_TOGGLE && p !== TOUCHTAB_TENDER_ID);

    const unaccepted = NON_CARD_TENDERS
        .filter((t) => (acceptedMethods & t.method) === 0)
        .map((t) => t.path);

    return ids.filter((p) => !unaccepted.includes(p));
}
